/* ============================================================
   CLIENTELE DISTRIBUTION HUB — JavaScript
   All dashboard logic, API calls, pre-fill launchers
   ============================================================ */

'use strict';

// ── CONFIG ────────────────────────────────────────────────
const HUB_API_BASE = '/api/admin';
const REFRESH_INTERVAL = 30000; // 30s auto-refresh

// ── STATE ─────────────────────────────────────────────────
let hubState = {
  authenticated: false,
  password: '',
  items: [],
  stats: {},
  platformStatus: {
    shopify: 'unknown',
    ebay: 'unknown',
    vendoo: 'unknown',
    stockx: 'manual',
    goat: 'manual',
  },
  activeFilter: 'all',
  activePlatformFilter: '',
  searchQuery: '',
  refreshTimer: null,
  selectedItem: null,
  prefillPlatform: null,
};

// ── DOM HELPERS ───────────────────────────────────────────
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const el = (tag, attrs = {}, ...children) => {
  const e = document.createElement(tag);
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === 'class') e.className = v;
    else if (k === 'html') e.innerHTML = v;
    else if (k.startsWith('on')) e.addEventListener(k.slice(2), v);
    else e.setAttribute(k, v);
  });
  children.forEach(c => {
    if (c == null) return;
    e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  });
  return e;
};

// ── TOAST ─────────────────────────────────────────────────
function showToast(msg, type = 'success', duration = 3500) {
  let toast = $('#hub-toast');
  if (!toast) {
    toast = el('div', { id: 'hub-toast', class: 'hub-toast' });
    document.body.appendChild(toast);
  }
  toast.textContent = msg;
  toast.className = `hub-toast ${type}`;
  toast.classList.add('show');
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => toast.classList.remove('show'), duration);
}

// ── AUTH ──────────────────────────────────────────────────
function initLockScreen() {
  const lockScreen = $('#hub-lock-screen');
  const hubMain    = $('#hub-main');
  const pwInput    = $('#hub-password-input');
  const lockBtn    = $('#hub-lock-btn');
  const lockError  = $('#hub-lock-error');

  function unlock(pw) {
    hubState.password = pw || 'preview';
    hubState.authenticated = true;
    sessionStorage.setItem('hub_pw', hubState.password);
    lockScreen.classList.add('hidden');
    hubMain.classList.add('visible');
    initDashboard();
  }

  // Check session storage first
  const sessionPw = sessionStorage.getItem('hub_pw');
  if (sessionPw) { unlock(sessionPw); return; }

  // Auto-unlock: probe the API with empty password.
  // If HUB_PASSWORD is not set on the server, API allows any key → auto-unlock for preview.
  fetch(`${HUB_API_BASE}/inventory`, {
    headers: { 'X-Hub-Password': 'preview' }
  }).then(r => {
    if (r.ok) {
      // No password configured — go straight in (dev/preview mode)
      unlock('preview');
    } else {
      // Password IS configured — show lock screen
      lockScreen.style.display = 'flex';
    }
  }).catch(() => {
    // Network error — show lock screen, let user type anything
    lockScreen.style.display = 'flex';
  });

  lockBtn.addEventListener('click', () => attemptUnlock());
  pwInput.addEventListener('keydown', e => { if (e.key === 'Enter') attemptUnlock(); });

  function attemptUnlock() {
    const pw = pwInput.value.trim();
    if (!pw) return;
    fetch(`${HUB_API_BASE}/inventory`, {
      headers: { 'X-Hub-Password': pw }
    }).then(r => {
      if (r.ok) {
        unlock(pw);
      } else {
        lockError.style.display = 'block';
        lockError.textContent = 'ACCESS DENIED. CHECK PASSWORD.';
        pwInput.value = '';
      }
    }).catch(() => { unlock(pw); });
  }
}

// ── AUTHENTICATED API CALL ────────────────────────────────
async function apiCall(method, path, body = null) {
  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-Hub-Password': hubState.password,
    },
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${HUB_API_BASE}${path}`, opts);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── INIT DASHBOARD ────────────────────────────────────────
function initDashboard() {
  initFilterBar();
  initSearch();
  initRefreshBtn();
  loadInventory();
  loadPlatformStatus();
  updateTimestamp();

  // Auto-refresh
  hubState.refreshTimer = setInterval(() => {
    loadInventory();
    loadPlatformStatus();
    updateTimestamp();
  }, REFRESH_INTERVAL);
}

function updateTimestamp() {
  const ts = $('#hub-timestamp');
  if (ts) ts.textContent = new Date().toLocaleTimeString('en-US', {
    hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit'
  }) + ' LOCAL';
}

// ── FILTER BAR ────────────────────────────────────────────
function initFilterBar() {
  $$('.filter-btn[data-filter]').forEach(btn => {
    btn.addEventListener('click', () => {
      $$('.filter-btn[data-filter]').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      hubState.activeFilter = btn.dataset.filter;
      renderInventory();
    });
  });

  $$('.filter-btn[data-platform]').forEach(btn => {
    btn.addEventListener('click', () => {
      const isActive = btn.classList.contains('active');
      $$('.filter-btn[data-platform]').forEach(b => b.classList.remove('active'));
      hubState.activePlatformFilter = isActive ? '' : btn.dataset.platform;
      if (!isActive) btn.classList.add('active');
      renderInventory();
    });
  });
}

function initSearch() {
  const search = $('#hub-search');
  if (!search) return;
  let searchTimer;
  search.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      hubState.searchQuery = search.value.trim().toLowerCase();
      renderInventory();
    }, 200);
  });
}

function initRefreshBtn() {
  const btn = $('#hub-refresh-btn');
  if (!btn) return;
  btn.addEventListener('click', () => {
    btn.textContent = 'REFRESHING...';
    Promise.all([loadInventory(), loadPlatformStatus()]).finally(() => {
      btn.textContent = 'REFRESH';
      updateTimestamp();
    });
  });
}

// ── LOAD INVENTORY ────────────────────────────────────────
async function loadInventory() {
  try {
    const data = await apiCall('GET', '/inventory');
    hubState.items = data.items || [];
    hubState.stats = data.stats || {};
    updateStats();
    renderInventory();
  } catch (err) {
    console.error('Failed to load inventory:', err);
    renderError('Failed to load inventory: ' + err.message);
  }
}

function updateStats() {
  const s = hubState.stats;
  const setPending = $('#stat-pending');
  const setActive  = $('#stat-active');
  const setSold    = $('#stat-sold');
  const setPlat    = $('#stat-platforms');
  if (setPending) setPending.textContent = s.pending_review ?? '—';
  if (setActive)  setActive.textContent  = s.active_listings ?? '—';
  if (setSold)    setSold.textContent    = s.sold_today ?? '—';
  if (setPlat)    setPlat.textContent    = s.total_platforms ?? 5;
}

// ── FILTER ITEMS ──────────────────────────────────────────
function getFilteredItems() {
  let items = [...hubState.items];
  const { activeFilter, activePlatformFilter, searchQuery } = hubState;

  if (activeFilter !== 'all') {
    items = items.filter(item => {
      switch (activeFilter) {
        case 'pending': return item.shopify_status === 'DRAFT';
        case 'active':  return item.shopify_status === 'LIVE';
        case 'sold':    return item.shopify_status === 'SOLD' || !!item.sold_on;
        default: return true;
      }
    });
  }

  if (activePlatformFilter) {
    items = items.filter(item => {
      switch (activePlatformFilter) {
        case 'ebay':    return item.ebay_status    === 'LIVE';
        case 'grailed': return item.grailed_status === 'LIVE';
        case 'stockx':  return item.stockx_status  === 'LIVE';
        case 'goat':    return item.goat_status    === 'LIVE';
        default: return true;
      }
    });
  }

  if (searchQuery) {
    items = items.filter(item =>
      item.title?.toLowerCase().includes(searchQuery) ||
      item.brand?.toLowerCase().includes(searchQuery)
    );
  }

  return items;
}

// ── RENDER INVENTORY TABLE ────────────────────────────────
function renderInventory() {
  const tbody = $('#inventory-tbody');
  if (!tbody) return;

  const items = getFilteredItems();

  if (items.length === 0) {
    tbody.innerHTML = `
      <tr><td colspan="10">
        <div class="hub-empty">
          <div class="hub-empty-text">NO ITEMS MATCH CURRENT FILTER</div>
          <div class="hub-empty-sub">Adjust filters or submit items via the Intake Form</div>
        </div>
      </td></tr>`;
    return;
  }

  tbody.innerHTML = '';
  items.forEach(item => tbody.appendChild(renderRow(item)));
}

function renderError(msg) {
  const tbody = $('#inventory-tbody');
  if (!tbody) return;
  tbody.innerHTML = `<tr><td colspan="10"><div class="hub-empty">
    <div class="hub-empty-text" style="color:var(--red)">${msg}</div>
  </div></td></tr>`;
}

// ── STATUS BADGE ──────────────────────────────────────────
function statusBadge(status) {
  const map = {
    'LIVE':    { cls: 'live',    label: 'LIVE' },
    'DRAFT':   { cls: 'draft',   label: 'DRAFT' },
    'PENDING': { cls: 'pending', label: 'PEND' },
    'SOLD':    { cls: 'sold',    label: 'SOLD' },
    'ERROR':   { cls: 'error',   label: 'ERR' },
    'NONE':    { cls: 'none',    label: '—' },
  };
  const s = map[status] || map['NONE'];
  const span = el('span', { class: `status-badge ${s.cls}` }, s.label);
  return span;
}

// ── RENDER ROW ────────────────────────────────────────────
function renderRow(item) {
  const isSold = item.shopify_status === 'SOLD' || !!item.sold_on;
  const tr = el('tr', { 'data-id': item.shopify_id });
  if (isSold) tr.style.opacity = '0.5';

  // ITEM
  const tdItem = el('td', {});
  const titleDiv = el('div', { class: 'item-title' }, item.title);
  const brandDiv = el('div', { class: 'item-brand' }, item.brand?.toUpperCase());
  tdItem.appendChild(titleDiv);
  tdItem.appendChild(brandDiv);

  // SIZE
  const tdSize  = el('td', {});
  const sizeDiv = el('div', { class: 'item-size' }, item.size || '—');
  tdSize.appendChild(sizeDiv);

  // PRICE
  const tdPrice = el('td', {});
  const priceDiv = el('div', { class: 'item-price' },
    item.price ? `$${Number(item.price).toLocaleString()}` : '—'
  );
  const condDiv = el('div', { class: 'item-condition' }, item.condition || '');
  tdPrice.appendChild(priceDiv);
  tdPrice.appendChild(condDiv);

  // PLATFORM STATUS CELLS
  const platforms = [
    { key: 'shopify_status',  label: 'SHOPIFY' },
    { key: 'ebay_status',     label: 'EBAY' },
    { key: 'grailed_status',  label: 'GRAILED' },
    { key: 'stockx_status',   label: 'STOCKX' },
    { key: 'goat_status',     label: 'GOAT' },
  ];

  const statusCells = platforms.map(({ key }) => {
    const td = el('td', { style: 'text-align:center' });
    td.appendChild(statusBadge(item[key] || 'NONE'));
    return td;
  });

  // ACTIONS
  const tdActions = el('td', {});
  const actionsDiv = el('div', { class: 'row-actions' });

  // LIST ALL button (only if not sold and not fully live)
  if (!isSold) {
    const listAllBtn = el('button', {
      class: 'row-btn',
      onclick: () => openListAllFlow(item),
    }, 'LIST ALL');
    actionsDiv.appendChild(listAllBtn);
  }

  // MARK SOLD dropdown
  if (!isSold) {
    const soldWrap = el('div', { class: 'sold-dropdown-wrap' });
    const soldBtn = el('button', {
      class: 'row-btn sold-btn',
      onclick: (e) => {
        e.stopPropagation();
        const dd = soldWrap.querySelector('.sold-dropdown');
        dd.classList.toggle('open');
      },
    }, 'SOLD ▾');

    const dropdown = el('div', { class: 'sold-dropdown' });
    ['stockx', 'goat', 'ebay', 'grailed', 'shopify'].forEach(platform => {
      const ddItem = el('div', {
        class: 'sold-dropdown-item',
        onclick: () => markSold(item, platform),
      }, `SOLD ON ${platform.toUpperCase()}`);
      dropdown.appendChild(ddItem);
    });

    soldWrap.appendChild(soldBtn);
    soldWrap.appendChild(dropdown);
    actionsDiv.appendChild(soldWrap);
  }

  // EDIT (opens Shopify admin)
  const editBtn = el('button', {
    class: 'row-btn',
    onclick: () => window.open(
      `https://admin.shopify.com/store/clientele-487/products/${item.shopify_id}`,
      '_blank'
    ),
  }, 'EDIT');
  actionsDiv.appendChild(editBtn);

  // MARKET DATA
  const mktBtn = el('button', {
    class: 'row-btn',
    onclick: () => openMarketPanel(item),
  }, 'MKT');
  actionsDiv.appendChild(mktBtn);

  // DELIST ALL
  if (!isSold) {
    const delistBtn = el('button', {
      class: 'row-btn danger',
      onclick: () => confirmDelistAll(item),
    }, 'DELIST');
    actionsDiv.appendChild(delistBtn);
  }

  tdActions.appendChild(actionsDiv);

  // Assemble row
  tr.appendChild(tdItem);
  tr.appendChild(tdSize);
  tr.appendChild(tdPrice);
  statusCells.forEach(td => tr.appendChild(td));
  tr.appendChild(tdActions);

  return tr;
}

// ── LIST ALL FLOW ─────────────────────────────────────────
function openListAllFlow(item) {
  // Open StockX and GOAT pre-fill panels, then launch both
  openPrefillPanel(item, 'stockx');
}

// ── STOCKX PRE-FILL LAUNCHER ──────────────────────────────
function openPrefillPanel(item, platform) {
  hubState.selectedItem = item;
  hubState.prefillPlatform = platform;

  const overlay = $('#prefill-overlay');
  if (!overlay) return;

  // Set platform header
  $('#prefill-platform-name').textContent = platform.toUpperCase();
  $('#prefill-title-label').textContent = platform === 'stockx'
    ? 'STOCKX LISTING DATA' : 'GOAT / ALIAS LISTING DATA';

  // Populate data grid
  const dataGrid = $('#prefill-data-grid');
  dataGrid.innerHTML = '';

  const rows = [
    { label: 'ITEM',      value: item.title },
    { label: 'BRAND',     value: item.brand },
    { label: 'SIZE',      value: item.size },
    { label: 'CONDITION', value: item.condition },
    { label: 'ASKING',    value: `$${Number(item.price).toLocaleString()}` },
    { label: 'HAS BOX',   value: item.has_box ? 'YES' : 'NO' },
  ];

  if (item.style_id) {
    rows.push({ label: 'STYLE ID', value: item.style_id });
  }

  rows.forEach(row => {
    const rowEl = el('div', { class: 'prefill-data-row' });
    rowEl.appendChild(el('span', { class: 'prefill-data-label' }, row.label));
    rowEl.appendChild(el('span', { class: 'prefill-data-value' }, row.value || '—'));
    dataGrid.appendChild(rowEl);
  });

  // Instructions
  const instrEl = $('#prefill-instructions');
  if (platform === 'stockx') {
    instrEl.textContent = 'Data copied to clipboard. Paste into StockX seller form. Verify all fields. Submit.';
  } else {
    instrEl.textContent = 'Download photos below. Open alias app (iOS/Android). Create listing manually. Upload photos.';
  }

  // Photos
  const photosGrid = $('#prefill-photos-grid');
  photosGrid.innerHTML = '';
  if (platform === 'goat' && item.images?.length) {
    item.images.forEach((src, i) => {
      const wrap = el('div', { class: 'prefill-photo-wrap' });
      const img = el('img', { src, alt: `Photo ${i+1}` });
      const dlBtn = el('button', {
        class: 'photo-download-btn',
        onclick: () => downloadPhoto(src, `${item.title}-photo-${i+1}`),
      }, 'DOWNLOAD');
      wrap.appendChild(img);
      wrap.appendChild(dlBtn);
      photosGrid.appendChild(wrap);
    });
    photosGrid.style.display = 'grid';
  } else {
    photosGrid.style.display = 'none';
  }

  // Reset copied badge
  const badge = $('#prefill-copied-badge');
  badge.classList.remove('visible');

  // Open button action
  const openBtn = $('#prefill-open-btn');
  openBtn.textContent = platform === 'stockx'
    ? 'OPEN STOCKX SELLER →'
    : 'OPEN GOAT / ALIAS →';
  openBtn.onclick = () => launchPlatform(item, platform);

  // Confirm button
  const confirmBtn = $('#prefill-confirm-btn');
  confirmBtn.textContent = platform === 'stockx'
    ? 'CONFIRM STOCKX LISTED ✓'
    : 'CONFIRM GOAT LISTED ✓';
  confirmBtn.onclick = () => confirmListed(item, platform);

  overlay.classList.add('open');

  // Auto-copy for StockX
  if (platform === 'stockx') {
    copyItemToClipboard(item);
  }
}

function launchPlatform(item, platform) {
  const url = platform === 'stockx'
    ? 'https://seller.stockx.com/inventory/list'
    : 'https://sell.alias.org';
  window.open(url, '_blank');
}

function copyItemToClipboard(item) {
  const text = [
    `Item: ${item.title}`,
    `Brand: ${item.brand}`,
    `Size: ${item.size}`,
    `Condition: ${item.condition}`,
    `Asking Price: $${item.price}`,
    item.style_id ? `Style ID: ${item.style_id}` : '',
  ].filter(Boolean).join('\n');

  navigator.clipboard.writeText(text).then(() => {
    const badge = $('#prefill-copied-badge');
    if (badge) badge.classList.add('visible');
    showToast('LISTING DATA COPIED TO CLIPBOARD');
  }).catch(() => {
    showToast('COPY FAILED — PASTE MANUALLY', 'warning');
  });
}

function downloadPhoto(src, filename) {
  const a = el('a', { href: src, download: filename + '.jpg' });
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

async function confirmListed(item, platform) {
  try {
    await apiCall('POST', '/confirm-listed', {
      shopify_product_id: item.shopify_id,
      platform,
    });
    showToast(`${platform.toUpperCase()} LISTING CONFIRMED`);
    closePrefillPanel();

    // If stockx confirmed, ask about GOAT
    if (platform === 'stockx') {
      setTimeout(() => {
        if (confirm('List on GOAT / alias now?')) {
          openPrefillPanel(item, 'goat');
        }
      }, 500);
    }

    loadInventory();
  } catch (err) {
    showToast('CONFIRM FAILED: ' + err.message, 'error');
  }
}

function closePrefillPanel() {
  const overlay = $('#prefill-overlay');
  if (overlay) overlay.classList.remove('open');
  hubState.selectedItem = null;
  hubState.prefillPlatform = null;
}

// ── MARK SOLD ─────────────────────────────────────────────
async function markSold(item, platform) {
  // Close any open dropdowns
  $$('.sold-dropdown.open').forEach(d => d.classList.remove('open'));

  const salePrice = prompt(`Enter sale price on ${platform.toUpperCase()} (leave blank to skip):`);

  // For automated platforms (eBay/Grailed), they self-delist via webhook.
  // For StockX/GOAT — manual confirmation triggers delist chain.
  if (platform === 'stockx' || platform === 'goat') {
    try {
      await apiCall('POST', '/mark-sold', {
        shopify_product_id: item.shopify_id,
        platform,
        sale_price: salePrice ? parseFloat(salePrice) : undefined,
      });
      showToast(`MARKED SOLD ON ${platform.toUpperCase()} — DELISTING ALL PLATFORMS`);
      loadInventory();
    } catch (err) {
      showToast('MARK SOLD FAILED: ' + err.message, 'error');
    }
  } else {
    showToast(`${platform.toUpperCase()} SALES AUTO-DETECTED VIA WEBHOOK`, 'warning');
  }
}

// ── DELIST ALL ────────────────────────────────────────────
async function confirmDelistAll(item) {
  if (!confirm(`EMERGENCY DELIST: Remove "${item.title}" from ALL platforms?\n\nThis cannot be undone.`)) return;

  try {
    await apiCall('POST', '/delist-all', {
      shopify_product_id: item.shopify_id,
    });
    showToast('DELISTED FROM ALL PLATFORMS');
    loadInventory();
  } catch (err) {
    showToast('DELIST FAILED: ' + err.message, 'error');
  }
}

// ── MARKET DATA PANEL ─────────────────────────────────────
async function openMarketPanel(item) {
  const sidebar = $('#market-data-sidebar');
  if (!sidebar) return;

  sidebar.innerHTML = `
    <div class="sidebar-title">MARKET INTELLIGENCE</div>
    <div class="hub-loading">FETCHING DATA</div>`;

  try {
    const data = await apiCall('GET', `/market-data/${item.shopify_id}`);
    const { market_data, recommendation, asking_price } = data;

    const formatPrice = (p) => p != null ? `$${Number(p).toLocaleString()}` : '—';
    const vsMarket = market_data?.stockx_last_sale
      ? asking_price - market_data.stockx_last_sale
      : null;
    const vsStr = vsMarket != null
      ? (vsMarket >= 0 ? `+$${vsMarket.toFixed(0)}` : `-$${Math.abs(vsMarket).toFixed(0)}`)
      : '—';

    const recClass = recommendation?.action?.includes('COMPETITIVE') ? 'competitive'
      : recommendation?.action?.includes('HOLD') ? 'hold' : 'above';

    sidebar.innerHTML = `
      <div class="sidebar-title">MARKET INTELLIGENCE</div>
      <div class="market-panel">
        <div class="market-row">
          <span class="market-label">STOCKX LOW ASK</span>
          <span class="market-value">${formatPrice(market_data?.stockx_lowest_ask)}</span>
        </div>
        <div class="market-row">
          <span class="market-label">STOCKX LAST SALE</span>
          <span class="market-value">${formatPrice(market_data?.stockx_last_sale)}</span>
        </div>
        <div class="market-row">
          <span class="market-label">GOAT LOW ASK</span>
          <span class="market-value">${formatPrice(market_data?.goat_lowest_ask)}</span>
        </div>
        <div class="market-row">
          <span class="market-label">EBAY RECENT</span>
          <span class="market-value">${formatPrice(market_data?.ebay_recent_sold)}</span>
        </div>
        <div class="market-row">
          <span class="market-label">YOUR PRICE</span>
          <span class="market-value green">$${Number(asking_price).toLocaleString()}</span>
        </div>
        <div class="market-row">
          <span class="market-label">VS MARKET</span>
          <span class="market-value ${vsMarket != null && vsMarket > 0 ? 'red' : vsMarket != null && vsMarket < 0 ? 'green' : 'muted'}">${vsStr}</span>
        </div>
        <div class="recommendation-box">
          <div class="rec-label">APOLLO RECOMMENDS</div>
          <div class="rec-action ${recClass}">${recommendation?.action || 'ANALYZING...'}</div>
          <div style="font-size:9px;color:var(--muted-text);margin-top:4px;letter-spacing:0.08em;">${recommendation?.reason || ''}</div>
        </div>
        ${!market_data?.stockx_lowest_ask ? `
        <div class="market-manual-links">
          <a class="market-check-link" href="https://stockx.com/search?s=${encodeURIComponent(item.title)}" target="_blank">CHECK STOCKX →</a>
          <a class="market-check-link" href="https://www.goat.com/search?query=${encodeURIComponent(item.title)}" target="_blank">CHECK GOAT →</a>
          <a class="market-check-link" href="https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(item.title)}&LH_Sold=1" target="_blank">CHECK EBAY SOLD →</a>
        </div>` : ''}
      </div>`;

  } catch (err) {
    sidebar.innerHTML = `
      <div class="sidebar-title">MARKET INTELLIGENCE</div>
      <div style="color:var(--red);font-size:11px;padding:12px;">
        FETCH FAILED: ${err.message}
      </div>
      <div class="market-manual-links">
        <a class="market-check-link" href="https://stockx.com/search?s=${encodeURIComponent(item.title)}" target="_blank">CHECK STOCKX →</a>
        <a class="market-check-link" href="https://www.goat.com/search?query=${encodeURIComponent(item.title)}" target="_blank">CHECK GOAT →</a>
        <a class="market-check-link" href="https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(item.title)}&LH_Sold=1" target="_blank">CHECK EBAY SOLD →</a>
      </div>`;
  }
}

// ── PLATFORM STATUS ───────────────────────────────────────
async function loadPlatformStatus() {
  try {
    const data = await apiCall('GET', '/platform-status');
    hubState.platformStatus = data;
    renderPlatformStatus();
  } catch {
    // Silently fail — don't disrupt dashboard
  }
}

function renderPlatformStatus() {
  const ps = hubState.platformStatus;
  const rows = [
    { id: 'ps-shopify', name: 'SHOPIFY',  status: ps.shopify },
    { id: 'ps-ebay',    name: 'EBAY',     status: ps.ebay },
    { id: 'ps-vendoo',  name: 'VENDOO',   status: ps.vendoo },
    { id: 'ps-stockx',  name: 'STOCKX',   status: 'manual' },
    { id: 'ps-goat',    name: 'GOAT',     status: 'manual' },
  ];

  const container = $('#platform-status-list');
  if (!container) return;

  container.innerHTML = '';
  rows.forEach(row => {
    const statusLabel = row.status === true  ? 'CONNECTED'
      : row.status === false ? 'ERROR'
      : row.status === 'manual' ? 'MANUAL MODE'
      : row.status === 'connected' ? 'CONNECTED'
      : 'CHECKING...';

    const indicatorClass = row.status === true || row.status === 'connected' ? 'connected'
      : row.status === false || row.status === 'error' ? 'error'
      : row.status === 'manual' ? 'manual'
      : 'unknown';

    const textClass = row.status === true || row.status === 'connected' ? 'green'
      : row.status === false || row.status === 'error' ? 'red'
      : row.status === 'manual' ? 'amber'
      : '';

    const rowEl = el('div', { class: 'platform-row', id: row.id });
    rowEl.appendChild(el('div', { class: `platform-indicator ${indicatorClass}` }));
    rowEl.appendChild(el('span', { class: 'platform-name' }, row.name));
    rowEl.appendChild(el('span', { class: `platform-status-text ${textClass}` }, statusLabel));
    container.appendChild(rowEl);
  });
}

// ── CLOSE DROPDOWNS ON OUTSIDE CLICK ─────────────────────
document.addEventListener('click', () => {
  $$('.sold-dropdown.open').forEach(d => d.classList.remove('open'));
});

// ── INIT ──────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  initLockScreen();

  // Close prefill overlay on click outside panel
  const overlay = $('#prefill-overlay');
  if (overlay) {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closePrefillPanel();
    });
  }

  const closeBtn = $('#prefill-close-btn');
  if (closeBtn) closeBtn.addEventListener('click', closePrefillPanel);
});
